import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Icon, Input, Popup, SemanticICONS, Tab } from 'semantic-ui-react';
import styled from 'styled-components';
import { useDebounce } from 'use-lodash-debounce';
import Layout from '~/components/Layout';
import { El } from '~/components/Layout/style';
import TableComponent from '~/components/TableComponent';
import { LIGHT_USER_ROLE_SLUG } from '~/Constants';
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery';
import { useAuth } from '~/routes/components/AuthProvider';
import { RouteTypes } from '~/routes/constants';
import { FiltersWrapper } from '../../DairyFactory/ActivityLogs/style';
import Export from './Export';
import FilterDropdown, { getDropdownFilterKeys } from './FilterDropdown';
import Filters from './Filters';
import DeleteTourModal from './TourModals/DeleteTourModal';
import { useHash } from '~/components/useHash';
import EditTourDate from './EditTourDate';
import ReloadTourModal from './TourModals/ReloadTourModal';
import CreateTourDrawer from './CreateTourDrawer';
import CustomIcon from '~/components/CustomIcon';
import ChangeLogsModal from './ShowTours/ChangeLogsModal';
import { handleErrors } from '~/utils/helpers';

const ActionWrapper = styled.div`
min-width: 300px !important;
max-height: 40px !important;
flex-wrap: wrap;
gap: 10px;
display: flex;
.ui.labeled.icon.button, .ui.labeled.icon.buttons .button {  
   padding-left: 3em !important;
}
 button {
   display: inherit !important;  
 }
`

const serializeFilters = (filters) => {
  const params = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key] !== '' && filters[key] !== 0 && key !== 'page' && key !== 'rows') {
      params.set(key, filters[key].toString());
    }
  });
  return params.toString();
};

export const deserializeFilters = (hash, defaultFilters) => {
  if (!hash || hash === '#') return defaultFilters;

  const hashValue = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(hashValue);
  const filters = { ...defaultFilters };

  params.forEach((value, key) => {
    if (key in filters) {
      if (key === 'page' || key === 'rows') {
        filters[key] = parseInt(value, 10) || defaultFilters[key];
      } else {
        filters[key] = value;
      }
    }
  });

  return filters;
};

const MilkDelivery: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [hash, updateHash] = useHash();
  const actions = [
    {
      popupMessage: t('Overview Tour Details'),
      type: 'link',
      name: 'eye',
      size: 'large',
      link: RouteTypes.MILK_MONEY_TOURS_SHOW
    },
    {
      popupMessage: 'Tourstart / Tourende ändern',
      type: 'icon',
      name: 'pencil',
      size: 'large',
      actionType: 'edit',
      verifiedActionKey: 'canBeEdited',
      verifiedAction: 1
    },
    {
      popupMessage: 'Tour löschen',
      type: 'icon',
      name: 'trash',
      size: 'large',
      actionType: 'delete',
      verifiedActionKey: 'deleted',
      verifiedAction: 0
    },
    {
      popupMessage: '',
      type: 'icon',
      name: 'comment',
      size: 'large',
      actionType: 'comment',
      renderActions: getActions
    },
    {
      popupMessage: 'Tour wiederherstellen',
      type: 'icon',
      name: 'refresh',
      size: 'large',
      actionType: 'reload',
      verifiedActionKey: 'deleted',
      verifiedAction: 1
    }
  ]
  const defaultFilters = {
    page: 0,
    rows: 30,
    search: '',
    status: '',
    column: '',
    sort: '',
    dateFrom: '',
    deleted: 0,
    dateTo: '',
    totalAmountFrom: '',
    totalAmountTo: '',
    driverNumber: '',
    vehicleNumber: '',
    tourNumber: '',
    month: '',
    sampleBottleNumber: ''
  };

  const [dataFiltered, setDataFiltered] = useState(() => {
    const filtersFromHash = deserializeFilters(hash, defaultFilters);
    return filtersFromHash;
  })
  const [isInitialized, setIsInitialized] = useState(false);
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const navigate = useNavigate();
  const [search, setSearch] = useState(() => {
    const filtersFromHash = deserializeFilters(hash, defaultFilters);
    return filtersFromHash.search || '';
  })
  const [openExport, setOpenExport] = useState(false)
  const [activeTabIndex, setActiveTabIndex] = useState(() => {
    const filtersFromHash = deserializeFilters(hash, defaultFilters);
    return filtersFromHash.status ? 0 : 0;
  })
  const CHECKED_FILTERS_STORAGE_KEY = 'milkDelivery_checkedFilters';

  const [checkedFilters, setCheckedFilters] = useState(() => {
    try {
      const stored = localStorage.getItem('milkDelivery_checkedFilters');
      if (stored) {
        const storedKeys = JSON.parse(stored) as string[];
        return getDropdownFilterKeys().filter(f => storedKeys.includes(f.key));
      }
    } catch (error) {
      handleErrors(error)
    }

    const filtersFromHash = deserializeFilters(hash, defaultFilters);
    const initialCheckedFilters = [];

    getDropdownFilterKeys().forEach(filter => {
      if (filtersFromHash[filter.key] && filtersFromHash[filter.key] !== '') {
        initialCheckedFilters.push(filter);
      }
    });

    return initialCheckedFilters;
  })
  const debounceSearchValue = useDebounce(search, 300)
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const defaultTableColumnHeaders = [
    {
      name: t('Date import'),
      key: 'createdAt',
      type: 'date',
      format: 'DD.MM.YYYY HH:mm',
      sorted: 'desc'
    },
    {
      name: t('Tour start'),
      key: 'dateStart',
      type: 'date',
      format: 'DD.MM.YYYY HH:MM',
      sorted: 'desc'
    },
    {
      name: t('Tour end'),
      key: 'dateEnd',
      type: 'date',
      format: 'DD.MM.YYYY HH:MM',
      sorted: 'desc'
    },
    {
      name: t('Collection truck no.'),
      key: 'vehicleNumber',
      sorted: 'desc'
    },
    {
      name: t('Tour no.'),
      key: 'tourNumber',
      sorted: 'desc'
    },
    {
      name: t('Driver no.'),
      key: 'driverNumber',
      sorted: 'desc'
    },
    {
      name: t('Total amount (in l)'),
      key: 'totalAmount',
      type: 'no-unit-number',
      decimalScale: 1,
      sorted: 'desc'
    },
    {
      name: t('Number of operations'),
      key: 'dataCount',
      sorted: 'desc'
    },
    {
      name: t('Number of samples'),
      key: 'samplesTotal',
      sorted: 'desc'
    },
    {
      name: t('Actions')
    }
  ]
  const [tableColumnHeaders, setTableColumnHeaders] = useState(defaultTableColumnHeaders)
  const [deleteTour, setDeleteTour] = useState(null)
  const [openCreateTour, setOpenCreateTour] = useState(false)
  const [editTourData, setEditTourData] = useState(null)
  const [reloadTour, setReloadTour] = useState(null)

  const {
    data: milkDeliveryOptions,
    isLoading: isBusyOptions,
    refetch: fetchOptions,
    isFetched: optionsFetched
  } = MilkMoneyQuery.getMilkMoneyToursOptions()

  const {
    isLoading: isBusyData,
    data: milkDeliveryData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = MilkMoneyQuery.getMilkMoneyToursList({
    ...dataFiltered,
    dateFrom: dataFiltered.dateFrom !== '' ? parseDate(dataFiltered.dateFrom) : '',
    dateTo: dataFiltered.dateTo !== '' ? parseDate(dataFiltered.dateTo) : '',
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  }, Boolean(dataFiltered.month !== ''));

  const {
    data: milkDeliveryCount,
    isLoading: isBusyCount,
    isFetching: isFetchingCount,
    refetch: fetchCount
  } = MilkMoneyQuery.getMilkMoneyToursCount({
    ...dataFiltered,
    dateFrom: dataFiltered.dateFrom !== '' ? parseDate(dataFiltered.dateFrom) : '',
    dateTo: dataFiltered.dateTo !== '' ? parseDate(dataFiltered.dateTo) : '',
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  }, Boolean(dataFiltered.month !== ''));

  useEffect(() => {
    setTableColumnHeaders(defaultTableColumnHeaders)
    fetchOptions()
    const tempCheck = [...checkedFilters]
    getDropdownFilterKeys().map(filter => {
      const index = tempCheck.findIndex(tempFilter => tempFilter.key === filter.key)
      if (index >= 0) {
        tempCheck[index].placeholder = filter.placeholder
      }
    })
    setCheckedFilters(tempCheck)
  }, [t])

  useEffect(() => {
    if (dataFiltered.month !== '') {
      fetchFilteredData();
      if (milkDeliveryOptions?.inCheckStatusAvailable) {
        fetchCount()
      }
    } else if (milkDeliveryOptions && dataFiltered.month === '') {
      setDataFiltered((prevData) => ({
        ...prevData,
        month: milkDeliveryOptions?.months[0]?.key
      }))
    }
  }, [dataFiltered, optionsFetched]);

  useEffect(() => {
    if (isInitialized && dataFiltered.month !== '') {
      const hashString = serializeFilters(dataFiltered);
      updateHash(hashString);
    }
  }, [dataFiltered, isInitialized]);

  useEffect(() => {
    if (milkDeliveryOptions && !isInitialized) {
      setIsInitialized(true);
      if (dataFiltered.status && milkDeliveryOptions.statuses) {
        const statusIndex = milkDeliveryOptions.statuses.findIndex(s => s.key === dataFiltered.status);
        if (statusIndex >= 0) {
          setActiveTabIndex(statusIndex);
        }
      }
    }
  }, [milkDeliveryOptions, isInitialized, dataFiltered.status]);

  useEffect(() => {
    setDataFiltered({
      ...dataFiltered,
      search: debounceSearchValue
    })
  }, [debounceSearchValue])

  function getActions(tour, actionType) {
    const action = actions.find(action => action.actionType === actionType)
    switch (actionType) {
      case 'comment':
        return tour.comment && tour.deleted === 1 && <Popup
          content={tour?.comment || ''}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            size='large'
          />}
        />
      default:
        return
    }
  }

  function parseDate(value) {
    const arrayOfValues = value.split('.')
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }

  function renderFilters() {
    return <FiltersWrapper>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Farmer / Supplier number')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <Dropdown
        selectOnBlur={false}
        placeholder={t('Delivery month tour')}
        selection
        value={dataFiltered.month}
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, month: data.value as string })}
        options={milkDeliveryOptions?.months?.map(option => ({ text: option.label, value: option.key })) || []}
      />
      <Filters
        onChangeDataFiltered={(dataFiltered) => setDataFiltered(dataFiltered)}
        milkDeliveryOptions={milkDeliveryOptions}
        checkedFilters={checkedFilters}
        dataFiltered={dataFiltered}
      />
    </FiltersWrapper>
  }

  function renderActions() {
    return <ActionWrapper>
      <El.PageHeaderActionButton
        size='large'
        onClick={() => setOpenCreateTour(true)}
      >
        {t('Tour erstellen')}
      </El.PageHeaderActionButton>
      <FilterDropdown
        checkedFilters={checkedFilters}
        handleCheckFilter={(checked, filter, indexFilter) => {
          let newCheckedFilters;
          if (checked) {
            const newArray = [...checkedFilters];
            newArray.splice(indexFilter, 0, filter);
            newCheckedFilters = newArray;
          } else {
            newCheckedFilters = checkedFilters.filter(filterChecked => filterChecked.key !== filter.key);
            setDataFiltered(prev => ({
              ...prev,
              [filter.key]: defaultFilters[filter.key] ?? ''
            }));
          }
          setCheckedFilters(newCheckedFilters);
          localStorage.setItem(CHECKED_FILTERS_STORAGE_KEY, JSON.stringify(newCheckedFilters.map(f => f.key)));
        }}
      />
      <ChangeLogsModal
        months={milkDeliveryOptions?.months || []}
      />
      <El.PageHeaderActionButton
        size='large'
        icon
        labelPosition={'left'}
        onClick={() => navigate(RouteTypes.MILK_MONEY_TOURS_IMPORT)}
      >
        <Icon name='cloud upload' />
        {t('import')}
      </El.PageHeaderActionButton>
      <El.PageHeaderActionButton
        size='large'
        icon
        labelPosition={'left'}
        onClick={() => setOpenExport(true)}
      >
        <Icon name='file excel' />
        {t('Export')}
      </El.PageHeaderActionButton>
    </ActionWrapper>
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'delete':
        return setDeleteTour(row)
      case 'edit':
        return setEditTourData(row)
      case 'reload':
        return setReloadTour(row)
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      actions={actions}
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleClickAction={handleActionClick}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = milkDeliveryOptions?.statuses.find((_, indexOption) => indexOption === activeIndex);
      setDataFiltered({
        ...dataFiltered,
        status: status.key,
        page: 0
      });
      setActiveTabIndex(activeIndex)
    }
  }

  function renderPanes() {
    const panes = milkDeliveryOptions?.statuses.map((option) => {
      const countIndex = milkDeliveryCount?.findIndex((count) => count.status == option.key);
      return {
        menuItem: `${option.label}  (${milkDeliveryCount[countIndex]?.count || 0})`,
        render: () => renderTable()
      };
    });
    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={activeTabIndex}
        panes={panes}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  function renderContent() {
    if (milkDeliveryOptions?.inCheckStatusAvailable && milkDeliveryCount) {
      return renderPanes()
    }

    return renderTable()
  }

  function renderExportDialog() {
    return <Export
      openExport={openExport}
      handleClose={() => setOpenExport(false)}
      exportTypes={milkDeliveryOptions?.exportTypes || []}
      months={milkDeliveryOptions?.months || []}
    />
  }

  function renderDeleteModal() {
    return <DeleteTourModal
      deleteTour={deleteTour}
      handleClose={() => setDeleteTour(null)}
      handleSubmitDelete={() => {
        setDeleteTour(null)
        fetchFilteredData()
        fetchCount()
      }}
    />
  }

  function renderCreateTourDrawer() {
    return <CreateTourDrawer
      isVisible={openCreateTour}
      handleHiddenDrawer={(shouldRefetch) => {
        setOpenCreateTour(false)
        if (shouldRefetch) {
          fetchFilteredData()
          fetchCount()
        }
      }}
    />
  }

  function renderEditTourDate() {
    return <EditTourDate
      tourData={editTourData}
      handleHiddenDrawer={() => setEditTourData(null)}
      handleSubmit={() => {
        setEditTourData(null)
        fetchFilteredData()
      }}
    />
  }

  function renderRestoreTour() {
    return <ReloadTourModal
      reloadTour={reloadTour}
      handleClose={() => setReloadTour(null)}
      handleSubmit={() => {
        setReloadTour(null)
        fetchFilteredData()
        fetchCount()
      }}
    />
  }

  const { filtered, rows } = milkDeliveryData ? milkDeliveryData : { filtered: 0, rows: [] };
  const loading = isBusyOptions || isBusyData || isBusyCount || isFetchingData || isFetchingCount

  return <Layout
    segmented
    isBusy={loading}
    title={t('Overview MSW Tours')}
    filters={renderFilters()}
    actions={!lightUserLogged && renderActions()}
  >
    {renderRestoreTour()}
    {renderEditTourDate()}
    {renderDeleteModal()}
    {renderCreateTourDrawer()}
    {milkDeliveryData && milkDeliveryOptions && renderContent()}
    {renderExportDialog()}
  </Layout>;
};

export default MilkDelivery;
